import { useCallback, useEffect, useMemo, useState } from "react";
import { isTestOrderNumber } from "@/lib/payments/mode";
import { createFileRoute } from "@tanstack/react-router";
import { type Order } from "@/lib/orders";
import { useAdminOrders, type AdminOrder } from "@/lib/admin/orders.cloud";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { useAdminMoney } from "@/lib/admin/settings";
import { logAction } from "@/lib/admin/audit/log";
import { normaliseOrderLines } from "@/lib/orders/pack-lines";
import { PackBreakdown } from "@/components/orders/PackBreakdown";
import { PackingList } from "@/components/orders/PackingList";
import { PackingSlip } from "@/components/orders/PackingSlip";
import { Printer } from "lucide-react";
import { OrderPaymentSection } from "@/components/admin/OrderPaymentSection";
import {
  changeOrderStatusFn,
  getOrderFulfilmentFn,
  listShippingPartnersFn,
  saveOrderShipmentFn,
} from "@/lib/admin/shipping/fulfilment.functions";
import {
  FULFILMENT_STATUSES,
  HAPPY_PATH,
  STATUS_LABELS,
  actionLabel,
  buildTrackingUrl,
  computeShippingCharge,
  exceptionStatuses,
  forwardStatus,
  shippingUnlocked,
  type FulfilmentStatus,
  type OrderShipment,
  type ShippingPartner,
  type StatusHistoryEntry,
} from "@/lib/admin/shipping/types";

const STATUSES = FULFILMENT_STATUSES;

function label(status?: string) {
  if (!status) return "—";
  return STATUS_LABELS[status as keyof typeof STATUS_LABELS] ?? status;
}

function fmtDate(v?: string | null) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function toDateInput(v?: string | null) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function fromDateInput(v: string) {
  return v ? new Date(`${v}T00:00:00.000Z`).toISOString() : null;
}

export const Route = createFileRoute("/admin/orders")({ component: OrdersPage });

function statusTone(s?: string) {
  if (s === "delivered" || s === "completed") return "success" as const;
  if (s === "cancelled" || s === "refunded" || s === "returned") return "danger" as const;
  if (s === "shipped" || s === "out_for_delivery") return "info" as const;
  return "warn" as const;
}

function OrdersPage() {
  const { orders, loading, error, reload } = useAdminOrders();
  const money = useAdminMoney();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState<AdminOrder | null>(null);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return orders.filter((o) => {
      if (status && (o.status ?? "confirmed") !== status) return false;
      if (t && !(o.id.toLowerCase().includes(t) || o.email.toLowerCase().includes(t) || o.shipping.fullName.toLowerCase().includes(t))) return false;
      return true;
    });
  }, [orders, q, status]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Orders" subtitle={`${orders.length} orders`} />
      <div className="flex flex-wrap items-center gap-2 border border-black/10 bg-white p-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order id, email, name" className="flex-1 min-w-[220px] border border-black/15 px-3 py-2 text-sm outline-none focus:border-black/50"/>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-black/15 px-2 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Placed</th>
              <th className="px-3 py-2">Total</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-black/50">Loading...</td></tr>}
            {!loading && error && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-red-700">Could not load orders: {error}</td></tr>}
            {!loading && filtered.map((o) => (
              <tr key={o.id} className="border-b border-black/5">
                <td className="px-3 py-2 font-mono text-[12px] text-black/80">{o.id}{isTestOrderNumber(o.id) && <TestBadge />}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-black">{o.shipping.fullName}</div>
                  <div className="text-[11px] text-black/50">{o.email}</div>
                </td>
                <td className="px-3 py-2 text-black/70">{new Date(o.createdAt).toLocaleString()}</td>
                <td className="px-3 py-2">{money(o.pricing.total)}</td>
                <td className="px-3 py-2"><StatusPill tone={statusTone(o.status)}>{label(o.status ?? "confirmed")}</StatusPill></td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setOpen(o)} className="border border-black/15 px-2 py-1 text-[11px] hover:border-black/50">View</button>
                </td>
              </tr>
            ))}
            {!loading && !error && filtered.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-black/50">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <OrderDrawer
          order={open}
          onClose={() => setOpen(null)}
          onSaved={reload}
          onLocalStatus={(s) => setOpen({ ...open, status: s as AdminOrder["status"] })}
        />
      )}
    </div>
  );
}

function OrderDrawer({
  order,
  onClose,
  onSaved,
  onLocalStatus,
}: {
  order: AdminOrder;
  onClose: () => void;
  onSaved: () => void;
  onLocalStatus: (s: string) => void;
}) {
  const money = useAdminMoney();
  const current = (order.status ?? "confirmed") as string;
  const orderLines = useMemo(() => normaliseOrderLines(order.lines), [order.lines]);
  const [history, setHistory] = useState<StatusHistoryEntry[]>([]);
  const [shipEvents, setShipEvents] = useState<
    { id: string; createdAt: string; actor: string | null; partnerName: string | null; deliveryTypeName: string | null; trackingNumber: string | null }[]
  >([]);
  const [shipment, setShipment] = useState<OrderShipment | null>(null);
  const [partners, setPartners] = useState<ShippingPartner[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getOrderFulfilmentFn({ data: { orderId: order.rowId } })
      .then((res) => {
        setHistory(res.history as StatusHistoryEntry[]);
        setShipment(res.shipment as OrderShipment | null);
        setShipEvents(res.shipmentEvents ?? []);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [order.rowId]);

  useEffect(() => {
    load();
    listShippingPartnersFn()
      .then((rows) => setPartners(rows as ShippingPartner[]))
      .catch(() => setPartners([]));
  }, [load]);

  const forward = forwardStatus(current);
  const exceptions = exceptionStatuses(current);

  /** Browser print of the hidden packing slip only (no PDF generation). */
  const printPackingSlip = () => {
    if (typeof document === "undefined") return;
    document.body.classList.add("cb16-printing-slip");
    const cleanup = () => document.body.classList.remove("cb16-printing-slip");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
    window.setTimeout(cleanup, 1500);
  };

  const applyStatus = async (nextStatus: string) => {
    if (!nextStatus || busy) return;
    setBusy(true);
    try {
      await changeOrderStatusFn({ data: { orderId: order.rowId, status: nextStatus, notes: note || undefined } });
      logAction({ entity: "order", entityId: order.id, action: "status_change", summary: `${label(current)} to ${label(nextStatus)}` });
      onLocalStatus(nextStatus);
      setNote("");
      load();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="h-full w-full max-w-3xl overflow-y-auto bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between border-b border-black/10 pb-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">Order</p>
            <h3 className="font-mono text-lg text-black">{order.id}{isTestOrderNumber(order.id) && <TestBadge />}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={printPackingSlip}
              className="inline-flex items-center gap-2 border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-black/70 hover:border-black hover:text-black"
            >
              <Printer className="h-3.5 w-3.5" aria-hidden="true" /> Print packing slip
            </button>
            <button onClick={onClose} aria-label="Close order" className="text-sm text-black/60 hover:text-black">x</button>
          </div>
        </div>

        {error && <p className="mb-3 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        <section className="mb-4 border border-black/10 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">Current status</p>
          <p className="mt-1 mb-3"><StatusPill tone={statusTone(current)}>{label(current)}</StatusPill></p>

          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">Next action</p>
          <div className="grid gap-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note (optional) — saved with this status change"
              className="w-full border border-black/15 px-2 py-2 text-sm"
            />
            <div className="flex flex-wrap items-center gap-2">
              {forward && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void applyStatus(forward)}
                  className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-white disabled:opacity-40"
                >
                  {busy ? "Updating..." : actionLabel(forward)}
                </button>
              )}
              {exceptions.map((s: FulfilmentStatus) => (
                <button
                  key={s}
                  type="button"
                  disabled={busy}
                  onClick={() => void applyStatus(s)}
                  className="border border-black/25 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-black/70 hover:border-black/60 hover:text-black disabled:opacity-40"
                >
                  {actionLabel(s)}
                </button>
              ))}
              {!forward && exceptions.length === 0 && (
                <span className="text-xs text-black/50">This order has reached a terminal status.</span>
              )}
            </div>
            {forward && (
              <p className="text-[11px] text-black/50">
                Lifecycle: {HAPPY_PATH.map((s) => label(s)).join(" → ")}
              </p>
            )}
          </div>
        </section>

        <section className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">Ship to</p>
            <p className="mt-1 text-sm text-black">{order.shipping.fullName}<br/>{order.shipping.line1}<br/>{order.shipping.city} {order.shipping.postalCode}<br/>{order.shipping.country}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">Payment</p>
            <p className="mt-1 text-sm text-black">{order.paymentMethod}{order.cardLast4 ? ` .... ${order.cardLast4}` : ""}</p>
            <p className="mt-1 text-xs text-black/60">{order.paymentStatus ?? "paid"}</p>
          </div>
        </section>

        <ShipmentSection
          orderId={order.rowId}
          orderSubtotal={order.pricing.subtotal}
          customerDeliveryId={order.delivery?.id ?? null}
          partners={partners}
          shipment={shipment}
          unlocked={shippingUnlocked(current)}
          onSaved={() => { load(); onSaved(); }}
        />

        <Timeline order={order} history={history} shipEvents={shipEvents} />

        <OrderPaymentSection orderId={order.rowId} />

        <PackingList lines={orderLines} />
        <PackingSlip
          orderId={order.id}
          placedAt={order.createdAt}
          email={order.email}
          address={order.shipping}
          lines={orderLines}
        />
        <section className="mb-4">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">Line items</p>
          <div className="border border-black/10">
            {orderLines.map((l) => (
              <div key={l.key} className="border-b border-black/5 px-3 py-2 text-sm last:border-0">
                <div className="flex items-center justify-between gap-3">
                  <span>{l.name} x {l.quantity}</span>
                  <span className="text-black/70">{money(l.lineTotal)}</span>
                </div>
                <PackBreakdown line={l} tone="muted" />
              </div>
            ))}
          </div>
        </section>
        <section className="border-t border-black/10 pt-3 text-sm">
          <Row label="Subtotal" value={money(order.pricing.subtotal)}/>
          <Row label="Shipping" value={money(order.pricing.shipping)}/>
          {order.pricing.discount > 0 && <Row label="Discount" value={`- ${money(order.pricing.discount)}`}/>}
          <Row label="Total" value={money(order.pricing.total)} bold/>
        </section>
      </div>
    </div>
  );
}
function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return <div className={`flex items-center justify-between py-1 ${bold ? "font-semibold text-black" : "text-black/70"}`}><span>{label}</span><span>{value}</span></div>;
}

/** Shipping partner + tracking capture. Carriers come from Shipping Settings. */
function ShipmentSection({
  orderId,
  orderSubtotal,
  customerDeliveryId,
  partners,
  shipment,
  unlocked,
  onSaved,
}: {
  orderId: string;
  orderSubtotal: number;
  /** Delivery method the customer chose and paid for at checkout (standard/express/boutique). */
  customerDeliveryId: string | null;
  partners: ShippingPartner[];
  shipment: OrderShipment | null;
  unlocked: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<OrderShipment | null>(shipment);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cod, setCod] = useState(false);
  const [insured, setInsured] = useState(false);

  useEffect(() => { setDraft(shipment); }, [shipment]);

  const value = draft ?? {
    orderId,
    partnerId: null, deliveryTypeId: null, partnerName: null, deliveryTypeName: null,
    trackingNumber: null, trackingUrl: null, dispatchDate: null, expectedDeliveryDate: null,
    actualDeliveryDate: null, shippingCost: null, deliveryNotes: null, packageWeight: null, packageCount: null,
  };
  const set = (patch: Partial<OrderShipment>) => setDraft({ ...value, ...patch });
  const partner = partners.find((p) => p.id === value.partnerId) ?? null;
  const deliveryType = (partner?.deliveryTypes ?? []).find((t) => t.id === value.deliveryTypeId) ?? null;

  // Auto-populate a freshly confirmed order: default courier, dispatch today,
  // expected delivery in 3 days. Everything stays editable before saving, and
  // tracking fields are deliberately left empty until dispatch.
  useEffect(() => {
    if (!unlocked || partners.length === 0) return;
    const current = draft;
    if (current?.partnerId || current?.dispatchDate || current?.expectedDeliveryDate) return;
    const fallback = current ?? {
      orderId,
      partnerId: null, deliveryTypeId: null, partnerName: null, deliveryTypeName: null,
      trackingNumber: null, trackingUrl: null, dispatchDate: null, expectedDeliveryDate: null,
      actualDeliveryDate: null, shippingCost: null, deliveryNotes: null, packageWeight: null, packageCount: null,
    };
    const def = partners.find((p) => p.isDefault && p.isActive) ?? null;
    const today = new Date();
    const eta = new Date(today);
    eta.setDate(eta.getDate() + 3);
    const types = (def?.deliveryTypes ?? []).filter((t) => t.isActive);
    // Prefer the courier service matching the customer's paid delivery method
    // (e.g. "Express" for Express Delivery), then the partner's default type.
    const matching = customerDeliveryId
      ? types.find((t) => t.name.trim().toLowerCase() === customerDeliveryId.toLowerCase()) ?? null
      : null;
    const defaultType = matching ?? types.find((t) => t.isDefault) ?? (types.length === 1 ? types[0] : null);
    setDraft({
      ...fallback,
      partnerId: def?.id ?? fallback.partnerId,
      deliveryTypeId: defaultType?.id ?? fallback.deliveryTypeId,
      dispatchDate: today.toISOString(),
      expectedDeliveryDate: eta.toISOString(),
    });
  }, [unlocked, partners, draft, orderId, customerDeliveryId]);

  const derivedCost = computeShippingCharge(deliveryType, {
    orderSubtotal,
    cashOnDelivery: cod,
    insured,
  });

  const save = async () => {
    setBusy(true);
    try {
      const tracking = value.trackingUrl || buildTrackingUrl(partner?.trackingUrlFormat, value.trackingNumber);
      await saveOrderShipmentFn({
        data: { ...value, orderId, trackingUrl: tracking || null, shippingCost: derivedCost, cashOnDelivery: cod, insured },
      });
      setError(null);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-4 border border-black/10 p-3">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">
        Fulfilment — courier, delivery type &amp; tracking
      </p>
      {!unlocked && (
        <p className="mb-2 text-xs text-black/50">
          Fulfilment details unlock once the order is Confirmed.
        </p>
      )}
      {error && <p className="mb-2 text-sm text-red-700">{error}</p>}
      <fieldset disabled={!unlocked} className="grid gap-2 md:grid-cols-2 disabled:opacity-50">
        <label className="grid gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Shipping partner</span>
          <select
            value={value.partnerId ?? ""}
            onChange={(e) => {
              const nextId = e.target.value || null;
              const nextPartner = partners.find((p) => p.id === nextId) ?? null;
              const types = (nextPartner?.deliveryTypes ?? []).filter((t) => t.isActive);
              const defaultType = types.find((t) => t.isDefault) ?? (types.length === 1 ? types[0] : null);
              set({ partnerId: nextId, deliveryTypeId: defaultType?.id ?? null });
            }}
            className="border border-black/15 px-2 py-2 text-sm"
          >
            <option value="">Select partner</option>
            {partners.filter((p) => p.isActive).map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.isDefault ? " (default)" : ""}</option>
            ))}
            {/* Keep an already-saved but now-inactive partner visible; never silently replace it. */}
            {partner && !partner.isActive && (
              <option key={partner.id} value={partner.id} disabled>{partner.name} (inactive)</option>
            )}
            {!partner && value.partnerId && value.partnerName && (
              <option value={value.partnerId} disabled>{value.partnerName} (removed)</option>
            )}
          </select>
        </label>
        <label className="grid gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Delivery type</span>
          <select value={value.deliveryTypeId ?? ""} onChange={(e) => set({ deliveryTypeId: e.target.value || null })} className="border border-black/15 px-2 py-2 text-sm">
            <option value="">Select delivery type</option>
            {(partner?.deliveryTypes ?? []).filter((t) => t.isActive).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}{t.isDefault ? " (default)" : ""}{t.estimatedTime ? ` — ${t.estimatedTime}` : ""} — ₹{t.basePrice}
              </option>
            ))}
          </select>
        </label>
        <Input label="Tracking number" value={value.trackingNumber ?? ""} onChange={(v) => set({ trackingNumber: v || null })}/>
        <Input label="Tracking URL" value={value.trackingUrl ?? buildTrackingUrl(partner?.trackingUrlFormat, value.trackingNumber)} onChange={(v) => set({ trackingUrl: v || null })}/>
        <Input label="Dispatch date" type="date" value={toDateInput(value.dispatchDate)} onChange={(v) => set({ dispatchDate: fromDateInput(v) })}/>
        <Input label="Expected delivery" type="date" value={toDateInput(value.expectedDeliveryDate)} onChange={(v) => set({ expectedDeliveryDate: fromDateInput(v) })}/>
        <Input label="Actual delivery" type="date" value={toDateInput(value.actualDeliveryDate)} onChange={(v) => set({ actualDeliveryDate: fromDateInput(v) })}/>
        <div className="grid gap-1">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Courier cost (internal, from delivery type — not charged to customer)</span>
          <p className="border border-black/10 bg-black/[0.03] px-2 py-2 text-sm">
            {deliveryType ? `₹${derivedCost}` : value.shippingCost === null ? "Select a delivery type" : `₹${value.shippingCost}`}
          </p>
          {deliveryType?.freeShippingThreshold !== null && deliveryType?.freeShippingThreshold !== undefined && (
            <span className="text-[10px] text-black/50">Free above ₹{deliveryType.freeShippingThreshold}</span>
          )}
        </div>
        <div className="flex flex-col justify-end gap-1 text-sm">
          {deliveryType?.codCharge ? (
            <label className="flex items-center gap-2"><input type="checkbox" checked={cod} onChange={(e) => setCod(e.target.checked)} /> Cash on delivery (+₹{deliveryType.codCharge})</label>
          ) : null}
          {deliveryType?.insuranceCharge ? (
            <label className="flex items-center gap-2"><input type="checkbox" checked={insured} onChange={(e) => setInsured(e.target.checked)} /> Insured (+₹{deliveryType.insuranceCharge})</label>
          ) : null}
        </div>
        <Input label="Package weight (kg)" value={value.packageWeight === null ? "" : String(value.packageWeight)} onChange={(v) => set({ packageWeight: v === "" ? null : Number(v) })}/>
        <Input label="Number of packages" value={value.packageCount === null ? "" : String(value.packageCount)} onChange={(v) => set({ packageCount: v === "" ? null : Number(v) })}/>
        <div className="md:col-span-2">
          <Input label="Delivery notes" value={value.deliveryNotes ?? ""} onChange={(v) => set({ deliveryNotes: v || null })}/>
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <button type="button" disabled={busy} onClick={() => void save()} className="border border-black bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white disabled:opacity-40">
            {busy ? "Saving..." : "Save shipping details"}
          </button>
          {value.trackingUrl && (
            <a href={value.trackingUrl} target="_blank" rel="noreferrer" className="text-[11px] underline">Open tracking link</a>
          )}
        </div>
      </fieldset>
    </section>
  );
}

function Input({ label: text, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className="grid gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">{text}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-2 py-2 text-sm"/>
    </label>
  );
}

/** Order timeline: creation + payment + every recorded status change. */
function Timeline({
  order,
  history,
  shipEvents = [],
}: {
  order: AdminOrder;
  history: StatusHistoryEntry[];
  shipEvents?: {
    id: string;
    createdAt: string;
    actor: string | null;
    partnerName: string | null;
    deliveryTypeName: string | null;
    trackingNumber: string | null;
  }[];
}) {
  const done = new Set(history.map((h) => h.status));
  done.add(order.status ?? "confirmed");
  const entries = [
    { key: "created", title: "Order created", at: order.createdAt, actor: order.email, notes: null as string | null },
    ...(order.paymentStatus === "paid" ? [{ key: "paid", title: "Payment received", at: order.createdAt, actor: "razorpay", notes: null as string | null }] : []),
    ...history.map((h) => ({
      key: h.id,
      title: `${h.previousStatus ? `${label(h.previousStatus)} → ` : ""}${label(h.status)}`,
      at: h.createdAt,
      actor: h.actorEmail ?? "admin",
      notes: h.notes,
    })),
    ...shipEvents.map((e) => ({
      key: e.id,
      title: "Shipment updated",
      at: e.createdAt,
      actor: e.actor ?? "admin",
      notes: [
        e.partnerName ? `Courier: ${e.partnerName}` : null,
        e.deliveryTypeName ? `Service: ${e.deliveryTypeName}` : null,
        e.trackingNumber ? `Tracking: ${e.trackingNumber}` : null,
      ]
        .filter(Boolean)
        .join(" · ") || null,
    })),
  ].sort((a, b) => (new Date(a.at).getTime() - new Date(b.at).getTime()));

  return (
    <section className="mb-4 border border-black/10 p-3">
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">Timeline</p>
      <ol className="flex flex-col gap-3">
        {entries.map((e) => (
          <li key={e.key} className="border-l-2 border-black/70 pl-3">
            <p className="text-sm font-medium text-black">{e.title}</p>
            <p className="text-[11px] text-black/60">{fmtDate(e.at)} · {e.actor}</p>
            {e.notes && <p className="mt-0.5 text-[11px] text-black/70">{e.notes}</p>}
          </li>
        ))}
      </ol>
      <div className="mt-4 flex flex-wrap gap-1">
        {HAPPY_PATH.map((s) => (
          <span key={s} className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${done.has(s) ? "bg-black text-white" : "bg-black/5 text-black/40"}`}>
            {label(s)}
          </span>
        ))}
      </div>
    </section>
  );
}

/** Marks Razorpay Test Mode orders: no real payment, excluded from revenue. */
function TestBadge() {
  return (
    <span title="Razorpay Test Mode order: no real payment; excluded from revenue" className="ml-2 inline-block bg-destructive px-1.5 py-0.5 align-middle font-sans text-[10px] font-bold uppercase tracking-widest text-destructive-foreground">
      Test
    </span>
  );
}
